import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { isDarkMode } from "../../utils/constants";

export const UpgradeMaxModal = ({
  open = false,
  onClose = () => {},
  onUpgrade = () => {},
  eyebrow = "Incite Max",
  title = "Unlock more simultaneous trade tracking.",
  description =
    "Upgrade to Max for additional autonomous simultaneous trade tracking.",
  bullets = [],
  footer = "Upgrade when you're ready for more advanced workflows.",
  primaryLabel = "Get Max",
  secondaryLabel = "Not now",
}) => {
  const validBullets = Array.isArray(bullets)
    ? bullets.filter((item) => typeof item === "string" && item.trim().length > 0)
    : [];
  const hasBullets = validBullets.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${isDarkMode ? "rgba(62,84,118,0.55)" : "rgba(163,189,233,0.55)"}`,
          background: isDarkMode
            ? "linear-gradient(180deg, #131a28 0%, #0d111b 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: { xs: 2.25, sm: 3 },
            pr: { xs: 5, sm: 6 },
            position: "relative",
            borderBottom: hasBullets
              ? `1px solid ${isDarkMode ? "rgba(68,85,112,0.45)" : "rgba(187,208,243,0.65)"}`
              : "none",
            background: isDarkMode
              ? "radial-gradient(120% 120% at 100% 0%, rgba(39,129,255,0.3) 0%, rgba(17,27,43,0) 62%)"
              : "radial-gradient(130% 130% at 100% 0%, rgba(47,127,255,0.2) 0%, rgba(255,255,255,0) 62%)",
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close upgrade dialog"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: isDarkMode ? "#D5E4FF" : "#23529A",
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Typography
            sx={{
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontWeight: 700,
              color: isDarkMode ? "#94B8F6" : "#2D66B7",
            }}
          >
            {eyebrow}
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              fontSize: { xs: "24px", sm: "30px" },
              lineHeight: 1.08,
              fontWeight: 700,
              color: isDarkMode ? "#F3F8FF" : "#0F294F",
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 1.1,
              fontSize: "14px",
              lineHeight: 1.6,
              color: isDarkMode ? "#A5BEDF" : "#335B8F",
              maxWidth: 520,
            }}
          >
            {description}
          </Typography>
        </Box>

        {hasBullets && (
          <Box sx={{ p: { xs: 2.25, sm: 3 }, display: "grid", gap: 1.2 }}>
            {validBullets.map((item, idx) => (
              <Box
                key={item}
                sx={{
                  p: 1.3,
                  borderRadius: 2,
                  border: `1px solid ${isDarkMode ? "rgba(85,105,138,0.4)" : "rgba(176,201,239,0.62)"}`,
                  backgroundColor: isDarkMode
                    ? "rgba(18,27,42,0.62)"
                    : "rgba(247,251,255,0.95)",
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#fff",
                    background:
                      "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
                    mt: 0.15,
                  }}
                >
                  {idx + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    lineHeight: 1.55,
                    color: isDarkMode ? "#E5EEFF" : "#123A67",
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            px: { xs: 2.25, sm: 3 },
            pt: hasBullets ? { xs: 0.75, sm: 1 } : { xs: 2, sm: 2.5 },
            pb: { xs: 2.25, sm: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography
            sx={{ fontSize: "12px", color: isDarkMode ? "#86A2CB" : "#567CAE" }}
          >
            {footer}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "999px",
                px: 2.5,
                whiteSpace: "nowrap",
                flexShrink: 0,
                borderColor: isDarkMode
                  ? "rgba(126,160,214,0.55)"
                  : "rgba(89,132,204,0.55)",
                color: isDarkMode ? "#DCEBFF" : "#23529A",
              }}
            >
              {secondaryLabel}
            </Button>
            <Button
              variant="contained"
              onClick={onUpgrade}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "999px",
                px: 2.5,
                whiteSpace: "nowrap",
                flexShrink: 0,
                background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
              }}
            >
              {primaryLabel}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeMaxModal;
